import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import CEMarkLogo from '../assets/ce-mark-logo.svg';
import { AISysRequirementList } from '../components/features/AISysRequirementList/AISysRequirementList';
import { AISystemOverviewTab } from '../components/features/AISystemOverviewTab/AISystemOverviewTab';
import { Documentation } from '../components/features/Documentation/Documentation';
import { MonitoringAuditLogging } from '../components/features/MonitoringAuditLogging/MonitoringAuditLogging';
import { RepositoryManagement } from '../components/features/RepositoryManagement/RepositoryManagement';
import Loader from '../components/ui/Loader/Loader';
import ModalDeleteConfirmation from '../components/ui/Modal/ModalDeleteConfirmation';
import type { TabItem } from '../components/ui/Tabs/Tabs';
import { Tabs } from '../components/ui/Tabs/Tabs';
import { PATHS } from '../constants/Paths.constants';
import { useAuthorization } from '../hooks/useAuthorization';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { LeftArrow, TrashCan } from '../icons/SvgIcons';
import { AppLayout } from '../layout/AppLayout';
import { DashboardLayout } from '../layout/DashboardLayout';
import {
  deleteAiSystem,
  fetchAISystems,
  getAISystemOverview,
  getCEMark
} from '../services/AISystemsService';
import {
  fetchCurrentOverviewFailure,
  fetchCurrentOverviewStart,
  fetchCurrentOverviewSuccess,
  fetchCurrentSystemSuccess,
  prepareCurrentAISystem
} from '../store/slices/aiSystemsSlice';
import { AISystemEntity, RiskClassification } from '../types/AISystems.types';
import { TruncatedText } from '../utils/TruncatedText';

export function AISystemDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { can, roleResolved } = useAuthorization();

  const {
    currentSystem,
    currentOverview,
    currentOverviewAiSystemId,
    currentOverviewLoading,
    systems
  } = useAppSelector((state) => state.aiSystems);
  const currentRouteOverview =
    currentOverviewAiSystemId === id ? currentOverview : null;
  const isOverviewPending = currentOverviewLoading || !currentRouteOverview;
  const selectedSystemFromList = useMemo(
    () => systems.find((system) => system.id === id) ?? null,
    [id, systems]
  );
  const currentRouteSystem =
    currentSystem?.id === id ? currentSystem : selectedSystemFromList;

  const canDeleteSystem = roleResolved && can('aiSystems.delete');

  console.log(roleResolved, canDeleteSystem, currentRouteSystem);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchSystemDetails = async () => {
      if (!id || currentSystem?.id === id) return;

      if (selectedSystemFromList) {
        dispatch(fetchCurrentSystemSuccess(selectedSystemFromList));
        return;
      }

      try {
        const response = await fetchAISystems();
        const selectedSystem = response.items?.find(
          (system) => system.id === id
        );

        if (selectedSystem) {
          dispatch(fetchCurrentSystemSuccess(selectedSystem));
        }
      } catch (error) {
        console.error('Failed to fetch AI system details:', error);
      }
    };

    fetchSystemDetails();
  }, [id, currentSystem?.id, selectedSystemFromList, dispatch]);

  useEffect(() => {
    let isActive = true;

    const fetchSystemData = async () => {
      if (!id) return;

      try {
        dispatch(prepareCurrentAISystem(id));
        dispatch(fetchCurrentOverviewStart());
        const overview = await getAISystemOverview(id);
        if (!isActive) return;
        dispatch(fetchCurrentOverviewSuccess(overview));
      } catch (error) {
        if (!isActive) return;
        console.error('Failed to fetch AI system overview:', error);
        dispatch(
          fetchCurrentOverviewFailure('Failed to fetch AI system overview')
        );
        navigate(PATHS.AI_SYSTEMS, { state: { showErrorToast: true } });
      }
    };

    fetchSystemData();

    return () => {
      isActive = false;
    };
  }, [id, dispatch, navigate]);

  // Refresh CE mark after successful Annex IV signing
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const event = params.get('event');

    if (event === 'signing_complete' && id) {
      const refreshCEMark = async () => {
        try {
          const ceMark = await getCEMark(id);
          if (!currentRouteOverview) return;
          dispatch(
            fetchCurrentOverviewSuccess({ ...currentRouteOverview, ceMark })
          );
        } catch (error) {
          console.error('Failed to fetch CE mark:', error);
        }
      };

      refreshCEMark();
    }
  }, [location.search, id, currentRouteOverview, dispatch]);

  const effectiveRiskClassification = currentRouteOverview
    ? (currentRouteOverview.riskClassification as RiskClassification | null)
    : currentRouteSystem?.riskClassification;
  const effectiveEntity = currentRouteOverview
    ? (currentRouteOverview.generalInfo.entityType as AISystemEntity)
    : currentRouteSystem?.entity;

  const isHighRisk = useMemo(() => {
    return (
      effectiveRiskClassification === RiskClassification.HIGH_RISK ||
      effectiveRiskClassification === RiskClassification.HIGH_RISK_EXCEPTION
    );
  }, [effectiveRiskClassification]);

  const allTabs: TabItem[] = useMemo(
    () => [
      {
        key: 'overview',
        label: 'Overview',
        content: <AISystemOverviewTab />
      },
      {
        key: 'requirement-list',
        label: 'Requirement List',
        content: <AISysRequirementList />
      },
      {
        key: 'repository-management',
        label: 'Repository Management',
        content: <RepositoryManagement />
      },
      {
        key: 'documentation',
        label: 'Documentation',
        content: <Documentation />
      },
      {
        key: 'monitoring-audit-logging',
        label: 'Monitoring & Audit Logging',
        content: <MonitoringAuditLogging />
      }
    ],
    []
  );

  const tabs = useMemo(() => {
    const riskFilteredTabs =
      (isHighRisk && effectiveEntity === AISystemEntity.PROVIDER) ||
      (isHighRisk && effectiveEntity === AISystemEntity.DEPLOYER)
        ? allTabs
        : allTabs.filter((tab) => tab.key === 'documentation');

    if (!roleResolved) return riskFilteredTabs;

    return riskFilteredTabs.filter((tab) => {
      if (tab.key === 'overview') return can('aiSystems.view');
      if (tab.key === 'requirement-list')
        return can('aiSystems.requirementList.view');
      if (tab.key === 'repository-management')
        return can('aiSystems.repository.view');
      if (tab.key === 'documentation')
        return can('aiSystems.documentation.view');
      if (tab.key === 'monitoring-audit-logging')
        return can('aiSystems.monitoring.view');
      return true;
    });
  }, [isHighRisk, effectiveEntity, allTabs, can, roleResolved]);

  const handleBack = () => {
    navigate(PATHS.AI_SYSTEMS);
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!id || !canDeleteSystem) return;

    setIsDeleting(true);
    try {
      await deleteAiSystem(id);
      setShowDeleteModal(false);
      navigate(PATHS.AI_SYSTEMS, { state: { showDeletedToast: true } });
    } catch (error) {
      console.error('Failed to delete AI system:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AppLayout>
      <DashboardLayout title="AI Systems">
        <div className="flex gap-4 flex-col w-full h-full bg-white rounded-lg p-4 border border-gray-250v2">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={handleBack}
              id="ai-system-detail-back-button"
              data-testid="ai-system-detail-back-button"
              className="flex gap-2 items-center"
            >
              {LeftArrow()}
              <div className="text-gray-950 font-medium flex gap-3">
                {isOverviewPending ? (
                  'Loading...'
                ) : (
                  <>
                    <TruncatedText
                      text={currentRouteSystem?.name || 'AI System'}
                      className="max-w-[calc(100vw-210px)] min-[1530px]:max-w-[calc(100vw-350px)]"
                    />
                    {currentRouteOverview?.ceMark && (
                      <span className="flex items-center gap-1">
                        <img
                          src={CEMarkLogo}
                          alt="CE Mark"
                          className="h-4 w-4"
                        />
                        {currentRouteOverview.ceMark.notifiedBodyNumber && (
                          <span className="font-medium text-base leading-[12px] text-[#18181A]">
                            {currentRouteOverview.ceMark.notifiedBodyNumber}
                          </span>
                        )}
                      </span>
                    )}
                  </>
                )}
              </div>
            </button>
            <button
              type="button"
              onClick={handleDeleteClick}
              id="ai-system-detail-delete-button"
              data-testid="ai-system-detail-delete-button"
              disabled={!canDeleteSystem}
              className={
                !canDeleteSystem ? 'cursor-not-allowed opacity-50' : ''
              }
            >
              {TrashCan('small')}
            </button>
          </div>
          <div className="w-full px-6 flex-1 flex flex-col">
            {isOverviewPending ? (
              <div className="flex items-center justify-center h-full">
                <Loader />
              </div>
            ) : (
              <Tabs tabs={tabs} basePath={`/ai-systems/${id}`} />
            )}
          </div>
        </div>
      </DashboardLayout>

      <ModalDeleteConfirmation
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete AI System?"
        boldedDescription="Are you sure you want to delete ai system? "
        description="Deleting the AI system will permanently remove all related data, including documents, requirements, risk assessments, repository connections, and monitoring logs. Any users assigned to this system will also lose access. This action cannot be undone."
        isLoading={isDeleting}
        deleteButtonText={['Delete AI System', 'Deleting']}
      />
    </AppLayout>
  );
}
